import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Navigate } from 'react-router-dom';
import AdminLayout from '../../../components/AdminLayout';
import ContentBox from '../../../components/ContentBox';

const PAGE_SIZE_OPTIONS = [25, 50, 100, 200];

function ShopPurchases({ userData }) {
    const [purchases, setPurchases] = useState([]);
    const [loading, setLoading] = useState(false);
    const [total, setTotal] = useState(0);
    const [refundingId, setRefundingId] = useState(null);
    const [bulkRefunding, setBulkRefunding] = useState(false);
    const [selectedIds, setSelectedIds] = useState([]);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(50);

    const [filters, setFilters] = useState({ user: '', item: '', startDate: '', endDate: '' });

    const loadPurchases = useCallback(async () => {
        try {
            setLoading(true);
            const apiUrl = process.env.REACT_APP_API_URL;
            const params = new URLSearchParams({
                limit: String(pageSize),
                offset: String((page - 1) * pageSize)
            });
            if (filters.user.trim()) params.set('user', filters.user.trim());
            if (filters.item.trim()) params.set('item', filters.item.trim());
            if (filters.startDate) params.set('startDate', filters.startDate);
            if (filters.endDate) params.set('endDate', filters.endDate);

            const response = await fetch(`${apiUrl}shop/admin/purchases?${params.toString()}`, {
                credentials: 'include'
            });
            const data = await response.json();
            if (data.success) {
                setPurchases(data.purchases);
                setTotal(data.total);
            }
        } catch (error) {
            console.error('Error loading purchases:', error);
        } finally {
            setLoading(false);
        }
    }, [filters, page, pageSize]);

    useEffect(() => {
        loadPurchases();
    }, [loadPurchases]);

    const totalPages = Math.max(1, Math.ceil(total / pageSize));

    useEffect(() => {
        if (page > totalPages) setPage(totalPages);
    }, [page, totalPages]);

    const refundableOnPage = useMemo(
        () => purchases.filter((p) => p.Status === 'completed' && p.Refundable),
        [purchases]
    );

    const selectedOnPage = useMemo(
        () => refundableOnPage.filter((p) => selectedIds.includes(p.PurchaseID)),
        [refundableOnPage, selectedIds]
    );

    const allPageSelected = refundableOnPage.length > 0 && selectedOnPage.length === refundableOnPage.length;

    const handleFilterChange = (field) => (e) => {
        setFilters((prev) => ({ ...prev, [field]: e.target.value }));
        setPage(1);
        setSelectedIds([]);
    };

    const clearFilters = () => {
        setFilters({ user: '', item: '', startDate: '', endDate: '' });
        setPage(1);
        setSelectedIds([]);
    };

    const handlePageSizeChange = (e) => {
        setPageSize(Number(e.target.value));
        setPage(1);
        setSelectedIds([]);
    };

    const toggleSelected = (purchaseId) => {
        setSelectedIds((prev) =>
            prev.includes(purchaseId) ? prev.filter((id) => id !== purchaseId) : [...prev, purchaseId]
        );
    };

    const toggleSelectAllOnPage = () => {
        const pageIds = refundableOnPage.map((p) => p.PurchaseID);
        setSelectedIds((prev) =>
            allPageSelected ? prev.filter((id) => !pageIds.includes(id)) : [...new Set([...prev, ...pageIds])]
        );
    };

    const goToPage = (nextPage) => {
        setPage(Math.min(Math.max(nextPage, 1), totalPages));
    };

    const handleRefund = async (purchase) => {
        const who = purchase.HabboName || purchase.UserID;
        const confirmed = window.confirm(
            `Refund "${purchase.ItemName}" for ${who}?\n\n` +
            `This will return ${purchase.StampCostPaid} framp card(s) to their balance and remove the item from their account.`
        );
        if (!confirmed) return;

        try {
            setRefundingId(purchase.PurchaseID);
            const apiUrl = process.env.REACT_APP_API_URL;
            const response = await fetch(`${apiUrl}shop/admin/purchases/${purchase.PurchaseID}/refund`, {
                method: 'POST',
                credentials: 'include'
            });
            const data = await response.json();
            if (data.success) {
                setSelectedIds((prev) => prev.filter((id) => id !== purchase.PurchaseID));
                await loadPurchases();
            } else {
                window.alert(data.message || 'Refund failed.');
            }
        } catch (error) {
            console.error('Error refunding purchase:', error);
            window.alert('Refund failed. Please try again.');
        } finally {
            setRefundingId(null);
        }
    };

    const handleBulkRefund = async () => {
        if (selectedOnPage.length === 0) return;

        const totalStamps = selectedOnPage.reduce((sum, p) => sum + (Number(p.StampCostPaid) || 0), 0);
        const confirmed = window.confirm(
            `Refund ${selectedOnPage.length} purchase${selectedOnPage.length !== 1 ? 's' : ''}?\n\n` +
            `Each will be refunded individually to its owner, returning ${totalStamps} framp card(s) in total ` +
            `and removing the item from each account.`
        );
        if (!confirmed) return;

        try {
            setBulkRefunding(true);
            const apiUrl = process.env.REACT_APP_API_URL;
            const response = await fetch(`${apiUrl}shop/admin/purchases/refund-bulk`, {
                method: 'POST',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ purchaseIds: selectedOnPage.map((p) => p.PurchaseID) })
            });
            const data = await response.json();

            if (data.success) {
                const failures = (data.results || []).filter((r) => !r.success);
                if (failures.length > 0) {
                    window.alert(
                        `${data.message}\n\nSkipped:\n` +
                        failures.map((f) => `• ${f.itemName || f.purchaseId}: ${f.message}`).join('\n')
                    );
                } else {
                    window.alert(data.message);
                }
                setSelectedIds([]);
                await loadPurchases();
            } else {
                window.alert(data.message || 'Bulk refund failed.');
            }
        } catch (error) {
            console.error('Error running bulk refund:', error);
            window.alert('Bulk refund failed. Please try again.');
        } finally {
            setBulkRefunding(false);
        }
    };

    if (!userData || !userData.hasAdminRole) {
        return <Navigate to="/" replace />;
    }

    const inputClass = 'border border-gray-300 rounded px-3 py-2 text-sm w-full';
    const pageButtonClass = 'px-3 py-1 rounded text-sm bg-gray-200 text-gray-800 hover:bg-gray-300 disabled:opacity-40 disabled:cursor-not-allowed';
    const rangeStart = total === 0 ? 0 : (page - 1) * pageSize + 1;
    const rangeEnd = Math.min(page * pageSize, total);

    return (
        <AdminLayout>
            <ContentBox theme="default" tint="lightskyblue" header="Purchase Log">
                <div className="mb-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                    <div>
                        <label className="block text-xs font-semibold text-gray-600 mb-1">User</label>
                        <input
                            type="text"
                            value={filters.user}
                            onChange={handleFilterChange('user')}
                            placeholder="Habbo name"
                            className={inputClass}
                        />
                    </div>
                    <div>
                        <label className="block text-xs font-semibold text-gray-600 mb-1">Item</label>
                        <input
                            type="text"
                            value={filters.item}
                            onChange={handleFilterChange('item')}
                            placeholder="Item name"
                            className={inputClass}
                        />
                    </div>
                    <div>
                        <label className="block text-xs font-semibold text-gray-600 mb-1">From date</label>
                        <input
                            type="date"
                            value={filters.startDate}
                            onChange={handleFilterChange('startDate')}
                            className={inputClass}
                        />
                    </div>
                    <div>
                        <label className="block text-xs font-semibold text-gray-600 mb-1">To date</label>
                        <input
                            type="date"
                            value={filters.endDate}
                            onChange={handleFilterChange('endDate')}
                            className={inputClass}
                        />
                    </div>
                </div>

                <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
                    <p className="text-gray-700">
                        Showing <strong>{rangeStart}-{rangeEnd}</strong> of <strong>{total}</strong> matching purchases
                    </p>
                    <div className="flex items-center gap-2">
                        <label className="text-xs font-semibold text-gray-600">Per page</label>
                        <select
                            value={pageSize}
                            onChange={handlePageSizeChange}
                            className="border border-gray-300 rounded px-2 py-1 text-sm"
                        >
                            {PAGE_SIZE_OPTIONS.map((size) => (
                                <option key={size} value={size}>{size}</option>
                            ))}
                        </select>
                        <button
                            onClick={clearFilters}
                            className="text-sm px-3 py-1 rounded bg-gray-200 text-gray-800 hover:bg-gray-300"
                        >
                            Clear filters
                        </button>
                    </div>
                </div>

                <div className="mb-4 flex flex-wrap items-center justify-between gap-3 bg-gray-100 border border-gray-200 rounded px-3 py-2">
                    <p className="text-sm text-gray-700">
                        {selectedOnPage.length} selected on this page
                        {refundableOnPage.length > 0 && ` (${refundableOnPage.length} refundable)`}
                    </p>
                    <div className="flex items-center gap-2">
                        <button
                            onClick={() => setSelectedIds([])}
                            disabled={selectedOnPage.length === 0 || bulkRefunding}
                            className="text-sm px-3 py-1 rounded bg-gray-200 text-gray-800 hover:bg-gray-300 disabled:opacity-40 disabled:cursor-not-allowed"
                        >
                            Clear selection
                        </button>
                        <button
                            onClick={handleBulkRefund}
                            disabled={selectedOnPage.length === 0 || bulkRefunding}
                            className={`text-sm px-3 py-1 rounded text-white ${
                                selectedOnPage.length === 0 || bulkRefunding
                                    ? 'bg-gray-400 cursor-not-allowed'
                                    : 'bg-red-600 hover:bg-red-700'
                            }`}
                        >
                            {bulkRefunding ? 'Refunding...' : `Refund selected (${selectedOnPage.length})`}
                        </button>
                    </div>
                </div>

                {loading ? (
                    <p>Loading...</p>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full border-collapse text-sm">
                            <thead>
                            <tr className="bg-gray-100">
                                <th className="border border-gray-300 px-3 py-2 text-left">
                                    <input
                                        type="checkbox"
                                        checked={allPageSelected}
                                        onChange={toggleSelectAllOnPage}
                                        disabled={refundableOnPage.length === 0}
                                    />
                                </th>
                                <th className="border border-gray-300 px-4 py-2 text-left">Date</th>
                                <th className="border border-gray-300 px-4 py-2 text-left">User</th>
                                <th className="border border-gray-300 px-4 py-2 text-left">Item</th>
                                <th className="border border-gray-300 px-4 py-2 text-left">Class</th>
                                <th className="border border-gray-300 px-4 py-2 text-left">Cost</th>
                                <th className="border border-gray-300 px-4 py-2 text-left">Discount</th>
                                <th className="border border-gray-300 px-4 py-2 text-left">Status</th>
                                <th className="border border-gray-300 px-4 py-2 text-left">Refund</th>
                            </tr>
                            </thead>
                            <tbody>
                            {purchases.map((purchase) => {
                                const canRefund = purchase.Status === 'completed' && purchase.Refundable;
                                return (
                                    <tr key={purchase.PurchaseID} className="hover:bg-gray-50">
                                        <td className="border border-gray-300 px-3 py-2">
                                            <input
                                                type="checkbox"
                                                checked={selectedIds.includes(purchase.PurchaseID)}
                                                onChange={() => toggleSelected(purchase.PurchaseID)}
                                                disabled={!canRefund}
                                            />
                                        </td>
                                        <td className="border border-gray-300 px-4 py-2">
                                            {new Date(purchase.PurchaseTimestamp).toLocaleString()}
                                        </td>
                                        <td className="border border-gray-300 px-4 py-2">
                                            {purchase.HabboName || purchase.UserID}
                                        </td>
                                        <td className="border border-gray-300 px-4 py-2">{purchase.ItemName}</td>
                                        <td className="border border-gray-300 px-4 py-2">
                                            <span className={`px-2 py-1 rounded text-xs ${
                                                purchase.ItemClass === 'badge' ? 'bg-purple-100 text-purple-800' :
                                                    purchase.ItemClass === 'giveaway_token' ? 'bg-green-100 text-green-800' :
                                                        'bg-blue-100 text-blue-800'
                                            }`}>
                                                {purchase.ItemClass}
                                            </span>
                                        </td>
                                        <td className="border border-gray-300 px-4 py-2">{purchase.StampCostPaid}</td>
                                        <td className="border border-gray-300 px-4 py-2">
                                            {purchase.DiscountApplied ? Number(purchase.DiscountApplied).toFixed(1) : '-'}
                                        </td>
                                        <td className="border border-gray-300 px-4 py-2">
                                            <span className={`px-2 py-1 rounded text-xs ${
                                                purchase.Status === 'completed' ? 'bg-green-100 text-green-800' :
                                                    purchase.Status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                                                        purchase.Status === 'refunded' ? 'bg-gray-200 text-gray-700' :
                                                            'bg-red-100 text-red-800'
                                            }`}>
                                                {purchase.Status}
                                            </span>
                                        </td>
                                        <td className="border border-gray-300 px-4 py-2">
                                            {canRefund ? (
                                                <button
                                                    onClick={() => handleRefund(purchase)}
                                                    disabled={refundingId === purchase.PurchaseID || bulkRefunding}
                                                    className={`px-3 py-1 rounded text-xs text-white ${
                                                        refundingId === purchase.PurchaseID || bulkRefunding
                                                            ? 'bg-gray-400 cursor-not-allowed'
                                                            : 'bg-red-600 hover:bg-red-700'
                                                    }`}
                                                >
                                                    {refundingId === purchase.PurchaseID ? 'Refunding...' : 'Refund'}
                                                </button>
                                            ) : purchase.Status === 'completed' ? (
                                                <span className="text-xs text-gray-500" title={purchase.RefundBlockReason || ''}>
                                                    {purchase.RefundBlockReason || 'Not refundable'}
                                                </span>
                                            ) : (
                                                <span className="text-xs text-gray-400">-</span>
                                            )}
                                        </td>
                                    </tr>
                                );
                            })}
                            </tbody>
                        </table>
                    </div>
                )}

                <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
                    <p className="text-sm text-gray-700">Page {page} of {totalPages}</p>
                    <div className="flex items-center gap-2">
                        <button onClick={() => goToPage(1)} disabled={page === 1 || loading} className={pageButtonClass}>
                            First
                        </button>
                        <button onClick={() => goToPage(page - 1)} disabled={page === 1 || loading} className={pageButtonClass}>
                            Previous
                        </button>
                        <button onClick={() => goToPage(page + 1)} disabled={page >= totalPages || loading} className={pageButtonClass}>
                            Next
                        </button>
                        <button onClick={() => goToPage(totalPages)} disabled={page >= totalPages || loading} className={pageButtonClass}>
                            Last
                        </button>
                    </div>
                </div>
            </ContentBox>
        </AdminLayout>
    );
}

export default ShopPurchases;
